import React, {Fragment, useState} from "react";
import {compose} from "redux";
import {connect} from "react-redux";
import {translate} from "react-i18next";
import {Button, Checkbox, Col, Icon, Modal, Radio, Row} from "antd";
import {
  GUTTER_MD,
  GUTTER_SM,
  MARGIN_MD,
  MODAL_WIDTH_MD,
  SPAN_HALF,
  TABLE_COL_MIN_WIDTH_ID,
  TABLE_COL_MIN_WIDTH_NAME
} from "../../styles/constants";
import EnhancedModal from "../../components/enhanced-modal";
import _ from "lodash";
import {DataLanguageConsumer} from "../../contexts/DataLanguage";
import {isArtefactValid} from "../../utils/artefactValidators";
import {reuseAction} from "../../utils/reduxReuse";
import {
  addDerivedItemSchemeItems,
  changeDerivedItemSchemeCheckbox,
  changeDerivedItemSchemeCreate,
  hideDerivedItemSchemeCreate,
  removeDerivedItemSchemeItems,
  resetDerivedItemSchemeState,
  selectDerivedItemSchemeItem,
  showDerivedItemSchemeCreate
} from "./actions";
import {getNodes} from "../../utils/tree";
import InfiniteScrollTable from "../../components/infinite-scroll-table";
import ArtefactMinForm, {ARTEFACT_MIN_FORM_MODE_CREATE} from "../../components/artefact-min-form";
import {
  NODES_CONFIG_NODE_GENERAL_ITEMS_VIEW_MODE_TABLE,
  NODES_CONFIG_NODE_GENERAL_ITEMS_VIEW_MODE_TREE
} from "../../scenes/configuration/nodes-config/node/GeneralForm";
import CheckableItemsTree from "../../components/checkable-items-tree";
import "./style.css";

const mapStateToProps = (state, {instanceState}) => ({
  appLang: state.app.language,
  derivedItemScheme: instanceState.derivedItemScheme,
  derivedItemSchemeItems: instanceState.derivedItemSchemeItems,
  derivedItemSchemeItemsTree: instanceState.derivedItemSchemeItemsTree,
  derivedItemSchemeItemsTreeMaxDepth: instanceState.derivedItemSchemeItemsTreeMaxDepth,
  selectedSourceItems: instanceState.selectedSourceItems,
  selectedTargetItems: instanceState.selectedTargetItems,
  importCheckbox: instanceState.importCheckbox,
  isCreateVisible: instanceState.isCreateVisible
});

const mapDispatchToProps = (dispatch, {instancePrefix}) => ({
  onItemSelect: (selectedArr, isSource) => dispatch(reuseAction(selectDerivedItemSchemeItem(selectedArr, isSource), instancePrefix)),
  onItemsAdd: (itemTree, childrenKey, forcePreserveHierarchy) =>
    dispatch(reuseAction(addDerivedItemSchemeItems(itemTree, childrenKey, forcePreserveHierarchy), instancePrefix)),
  onItemsRemove: (items, childrenKey) => dispatch(reuseAction(removeDerivedItemSchemeItems(items, childrenKey), instancePrefix)),
  onCheckboxChange: checkbox => dispatch(reuseAction(changeDerivedItemSchemeCheckbox(checkbox), instancePrefix)),
  onCreateShow: () => dispatch(reuseAction(showDerivedItemSchemeCreate(), instancePrefix)),
  onCreateHide: () => dispatch(reuseAction(hideDerivedItemSchemeCreate(), instancePrefix)),
  onCreateChange: fields => dispatch(reuseAction(changeDerivedItemSchemeCreate(fields), instancePrefix)),
  onStateReset: () => dispatch(reuseAction(resetDerivedItemSchemeState(), instancePrefix))
});

const isItemsRemoveValid = (totalItems, itemsToRemove) => {

  let items = _.cloneDeep(totalItems);
  items = items.filter(({id}) => !itemsToRemove.includes(id));

  const mappedArr = {};
  items.forEach(el => {
    el.children = [];
    mappedArr[el.id] = el;
  });

  let ret = true;
  items.forEach(el => {
    if (el.parent) {
      if (!mappedArr[el.parent]) {
        ret = false
      }
    }
  });

  return ret;
};

const TABLE_HEIGHT = 300;

const ReduxDerivedItemScheme = ({
                                  t,
                                  appLang,
                                  itemTree,
                                  viewMode,
                                  childrenKey,
                                  agencies,
                                  isCreateVisible,
                                  derivedItemScheme,
                                  derivedItemSchemeItems,
                                  derivedItemSchemeItemsTree,
                                  derivedItemSchemeItemsTreeMaxDepth,
                                  selectedSourceItems,
                                  selectedTargetItems,
                                  importCheckbox,
                                  isImportParentDisabled,
                                  onItemSelect,
                                  onItemsAdd,
                                  onItemsRemove,
                                  onCheckboxChange,
                                  onCreateShow,
                                  onCreateChange,
                                  onCreateHide,
                                  onCreateSubmit,
                                  onStateReset,
                                  onViewModeChange,
                                  itemTreeMaxDepth,
                                  title
                                }) => {

  const [isVisible, setIsVisible] = useState(false);

  return (
    <DataLanguageConsumer>
      {dataLanguage => {

        const columns = [
          {
            title: t('data.item.id.shortLabel'),
            dataIndex: 'id',
            minWidth: TABLE_COL_MIN_WIDTH_ID
          },
          {
            title: t('data.item.name.shortLabel'),
            dataIndex: 'name',
            minWidth: TABLE_COL_MIN_WIDTH_NAME
          },
          {
            title: t('data.item.parent.shortLabel'),
            dataIndex: 'parent',
            minWidth: TABLE_COL_MIN_WIDTH_ID
          }
        ];

        const lang = dataLanguage || appLang;

        return (
          <Fragment>
            <EnhancedModal
              fullscreen
              visible={isVisible}
              title={title}
              onCancel={() => setIsVisible(false)}
              footer={
                <div>
                  <Button onClick={() => setIsVisible(false)} icon="zoom-out">
                    {t('commons.buttons.close.title')}
                  </Button>
                </div>
              }
            >
              <div style={{height: "100%"}}>
                <Row type="flex" justify="space-between" align="middle" gutter={32}
                     style={{marginBottom: MARGIN_MD}}>
                  <Col>
                    <Row type="flex" gutter={16}>
                      <Col>
                        <Radio.Group
                          value={viewMode}
                          onChange={({target}) => onViewModeChange(target.value)}
                        >
                          <Radio.Button value={NODES_CONFIG_NODE_GENERAL_ITEMS_VIEW_MODE_TABLE}>
                            <div
                              title={t('reduxComponents.derivedItemsScheme.viewModeRadio.table.title')}>
                              <Icon type="table"/>
                            </div>
                          </Radio.Button>
                          <Radio.Button value={NODES_CONFIG_NODE_GENERAL_ITEMS_VIEW_MODE_TREE}>
                            <div
                              title={t('reduxComponents.derivedItemsScheme.viewModeRadio.tree.title')}>
                              <Icon type="branches" style={{transform: "rotate(90deg)"}}/>
                            </div>
                          </Radio.Button>
                        </Radio.Group>
                      </Col>
                      <Col>
                        <Button onClick={() => setIsVisible(false)} icon="zoom-out">
                          {t('reduxComponents.derivedItemsScheme.buttons.zoom.title')}
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                  <Col>
                    {viewMode === NODES_CONFIG_NODE_GENERAL_ITEMS_VIEW_MODE_TABLE && (
                      <Row type="flex" justify="end" align="middle" gutter={32}>
                        <Col>
                          <Row type="flex" justify="start" align="middle" gutter={GUTTER_MD}>
                            <Col>
                              {t("reduxComponents.derivedItemsScheme.preservesHierarchy.label") + ":"}
                            </Col>
                            <Col>
                              <Checkbox
                                checked={importCheckbox.preserveHierarchy}
                                onChange={e => onCheckboxChange({preserveHierarchy: e.target.checked})}
                                disabled={isImportParentDisabled}
                              />
                            </Col>
                          </Row>
                        </Col>
                        {
                          importCheckbox.preserveHierarchy && (
                            <Col>
                              <Row type="flex" justify="start" align="middle" gutter={GUTTER_MD}>
                                <Col>
                                  {t("reduxComponents.derivedItemsScheme.autoImport.label") + ":"}
                                </Col>
                                <Col>
                                  {t("reduxComponents.derivedItemsScheme.autoImport.parents.label") + ":"}
                                </Col>
                                <Col>
                                  <Checkbox
                                    checked={importCheckbox.importParents}
                                    onChange={e => onCheckboxChange({importParents: e.target.checked})}
                                    disabled={isImportParentDisabled}
                                  />
                                </Col>
                                <Col>
                                  {t("reduxComponents.derivedItemsScheme.autoImport.children.label") + ":"}
                                </Col>
                                <Col>
                                  <Checkbox
                                    checked={importCheckbox.importChildren}
                                    onChange={e => onCheckboxChange({importChildren: e.target.checked})}
                                    disabled={isImportParentDisabled}
                                  />
                                </Col>
                                <Col>
                                  {t("reduxComponents.derivedItemsScheme.autoImport.descendants.label") + ":"}
                                </Col>
                                <Col>
                                  <Checkbox
                                    checked={importCheckbox.importDescendants}
                                    onChange={e => onCheckboxChange({importDescendants: e.target.checked})}
                                    disabled={isImportParentDisabled}
                                  />
                                </Col>
                              </Row>
                            </Col>
                          )}
                      </Row>
                    )}
                  </Col>
                </Row>
                <div
                  className="redux-derived-item-scheme-viewer redux-derived-item-scheme-viewer__fullscreen">
                  {viewMode === NODES_CONFIG_NODE_GENERAL_ITEMS_VIEW_MODE_TREE
                    ? (
                      <Row type="flex" gutter={GUTTER_MD}
                           style={{height: "100%"}}>
                        <Col span={SPAN_HALF}>
                          <CheckableItemsTree
                            itemTree={itemTree}
                            childrenKey={childrenKey}
                            rightActions={
                              <Button
                                onClick={() => onItemsAdd(itemTree, childrenKey, true)}
                                icon="double-right"
                                type="primary"
                                disabled={!selectedSourceItems || selectedSourceItems.length === 0}
                              >
                                {t('reduxComponents.derivedItemsScheme.buttons.addItems.title')}
                              </Button>
                            }
                            itemTreeMaxDepth={itemTreeMaxDepth}
                            selectedItems={selectedSourceItems || []}
                            onSelectedItemsChange={selectedArr => onItemSelect(selectedArr, true)}
                            getIsDisabledNode={({id}) => (derivedItemSchemeItems || []).map(({id}) => id).includes(id)}
                          />
                        </Col>
                        <Col span={SPAN_HALF}>
                          <CheckableItemsTree
                            itemTree={
                              (derivedItemSchemeItemsTree || []).filter(item =>
                                getNodes(
                                  derivedItemSchemeItemsTree,
                                  childrenKey,
                                  node => node[childrenKey].find(child => child.id === item.id) !== undefined
                                ).length === 0
                              )
                            }
                            childrenKey={childrenKey}
                            rightActions={
                              <Button
                                onClick={() => {
                                  if (isItemsRemoveValid(derivedItemSchemeItems, selectedTargetItems)) {
                                    onItemsRemove(selectedTargetItems, childrenKey);
                                  } else {
                                    Modal.warning({
                                      title: t('reduxComponents.derivedItemsScheme.modals.warning.delete.title')
                                    })
                                  }
                                }}
                                icon="delete"
                                type="primary"
                                disabled={!selectedTargetItems || selectedTargetItems.length === 0}
                              >
                                {t('reduxComponents.derivedItemsScheme.buttons.removeItems.title')}
                              </Button>
                            }
                            itemTreeMaxDepth={derivedItemSchemeItemsTreeMaxDepth}
                            selectedItems={selectedTargetItems || []}
                            onSelectedItemsChange={selectedArr => onItemSelect(selectedArr, false)}
                          />
                        </Col>
                      </Row>
                    )
                    : (
                      <Row type="flex" gutter={GUTTER_MD}>
                        <Col span={SPAN_HALF}>
                          <InfiniteScrollTable
                            data={getNodes(itemTree, childrenKey, () => true)}
                            getRowKey={({id}) => id}
                            columns={columns}
                            multilangStrDataIndexes={["name"]}
                            height={document.body.clientHeight - 357}
                            searchBarSpan={10}
                            rightActions={
                              <Button
                                onClick={() => onItemsAdd(itemTree, childrenKey)}
                                icon="double-right"
                                type="primary"
                                disabled={!selectedSourceItems || selectedSourceItems.length === 0}
                              >
                                {t('reduxComponents.derivedItemsScheme.buttons.addItems.title')}
                              </Button>
                            }
                            rowSelection={{
                              selectedRowKeys: (selectedSourceItems || []),
                              onChange: selectedArr => onItemSelect(selectedArr, true)
                            }}
                            getIsDisabledRow={({id}) => (derivedItemSchemeItems || []).map(({id}) => id).includes(id)}
                          />
                        </Col>
                        <Col span={SPAN_HALF}>
                          <InfiniteScrollTable
                            data={_.sortBy((derivedItemSchemeItems || []), [`order[${lang}]`])}
                            getRowKey={({id}) => id}
                            columns={columns}
                            multilangStrDataIndexes={["name"]}
                            height={document.body.clientHeight - 357}
                            searchBarSpan={10}
                            rightActions={
                              <Button
                                onClick={() => {
                                  if (isItemsRemoveValid(derivedItemSchemeItems, selectedTargetItems)) {
                                    onItemsRemove(selectedTargetItems, childrenKey);
                                  } else {
                                    Modal.warning({
                                      title: t('reduxComponents.derivedItemsScheme.modals.warning.delete.title')
                                    })
                                  }
                                }}
                                icon="delete"
                                type="primary"
                                disabled={!selectedTargetItems || selectedTargetItems.length === 0}
                              >
                                {t('reduxComponents.derivedItemsScheme.buttons.removeItems.title')}
                              </Button>
                            }
                            rowSelection={{
                              selectedRowKeys: (selectedTargetItems || []),
                              onChange: selectedArr => onItemSelect(selectedArr, false)
                            }}
                          />
                        </Col>
                      </Row>
                    )}
                </div>
              </div>
            </EnhancedModal>
            <div>
              <div>
                <Row type="flex" justify="space-between" align="middle" gutter={32}
                     style={{marginBottom: MARGIN_MD}}>
                  <Col>
                    <Row type="flex" align="middle" gutter={16}>
                      <Col>
                        <Radio.Group
                          value={viewMode}
                          onChange={({target}) => onViewModeChange(target.value)}
                        >
                          <Radio.Button value={NODES_CONFIG_NODE_GENERAL_ITEMS_VIEW_MODE_TABLE}>
                            <div
                              title={t('reduxComponents.derivedItemsScheme.viewModeRadio.table.title')}>
                              <Icon type="table"/>
                            </div>
                          </Radio.Button>
                          <Radio.Button value={NODES_CONFIG_NODE_GENERAL_ITEMS_VIEW_MODE_TREE}>
                            <div
                              title={t('reduxComponents.derivedItemsScheme.viewModeRadio.tree.title')}>
                              <Icon type="branches" style={{transform: "rotate(90deg)"}}/>
                            </div>
                          </Radio.Button>
                        </Radio.Group>
                      </Col>
                      <Col>
                        <Button onClick={() => setIsVisible(true)} icon="zoom-in">
                          {t('reduxComponents.derivedItemsScheme.buttons.zoom.title')}
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                  <Col>
                    {viewMode === NODES_CONFIG_NODE_GENERAL_ITEMS_VIEW_MODE_TABLE && (
                      <Row type="flex" justify="end" align="middle" gutter={32}>
                        <Col>
                          <Row type="flex" justify="start" align="middle" gutter={GUTTER_MD}>
                            <Col>
                              {t("reduxComponents.derivedItemsScheme.preservesHierarchy.label") + ":"}
                            </Col>
                            <Col>
                              <Checkbox
                                checked={importCheckbox.preserveHierarchy}
                                onChange={e => onCheckboxChange({preserveHierarchy: e.target.checked})}
                                disabled={isImportParentDisabled}
                              />
                            </Col>
                          </Row>
                        </Col>
                        {
                          importCheckbox.preserveHierarchy && (
                            <Col>
                              <Row type="flex" justify="start" align="middle" gutter={GUTTER_MD}>
                                <Col>
                                  {t("reduxComponents.derivedItemsScheme.autoImport.label") + ":"}
                                </Col>
                                <Col>
                                  {t("reduxComponents.derivedItemsScheme.autoImport.parents.label") + ":"}
                                </Col>
                                <Col>
                                  <Checkbox
                                    checked={importCheckbox.importParents}
                                    onChange={e => onCheckboxChange({importParents: e.target.checked})}
                                    disabled={isImportParentDisabled}
                                  />
                                </Col>
                                <Col>
                                  {t("reduxComponents.derivedItemsScheme.autoImport.children.label") + ":"}
                                </Col>
                                <Col>
                                  <Checkbox
                                    checked={importCheckbox.importChildren}
                                    onChange={e => onCheckboxChange({importChildren: e.target.checked})}
                                    disabled={isImportParentDisabled}
                                  />
                                </Col>
                                <Col>
                                  {t("reduxComponents.derivedItemsScheme.autoImport.descendants.label") + ":"}
                                </Col>
                                <Col>
                                  <Checkbox
                                    checked={importCheckbox.importDescendants}
                                    onChange={e => onCheckboxChange({importDescendants: e.target.checked})}
                                    disabled={isImportParentDisabled}
                                  />
                                </Col>
                              </Row>
                            </Col>
                          )}
                      </Row>
                    )}
                  </Col>
                </Row>
                <div
                  className="redux-derived-item-scheme-viewer">
                  {viewMode === NODES_CONFIG_NODE_GENERAL_ITEMS_VIEW_MODE_TREE
                    ? (
                      <Row type="flex" gutter={GUTTER_MD}
                           style={{marginBottom: MARGIN_MD}}>
                        <Col span={SPAN_HALF}>
                          <CheckableItemsTree
                            itemTree={itemTree}
                            childrenKey={childrenKey}
                            rightActions={
                              <Button
                                onClick={() => onItemsAdd(itemTree, childrenKey, true)}
                                icon="double-right"
                                type="primary"
                                disabled={!selectedSourceItems || selectedSourceItems.length === 0}
                              >
                                {t('reduxComponents.derivedItemsScheme.buttons.addItems.title')}
                              </Button>
                            }
                            itemTreeMaxDepth={itemTreeMaxDepth}
                            selectedItems={selectedSourceItems || []}
                            onSelectedItemsChange={selectedArr => onItemSelect(selectedArr, true)}
                            getIsDisabledNode={({id}) => (derivedItemSchemeItems || []).map(({id}) => id).includes(id)}
                          />
                        </Col>
                        <Col span={SPAN_HALF}>
                          <CheckableItemsTree
                            itemTree={
                              (derivedItemSchemeItemsTree || []).filter(item =>
                                getNodes(
                                  derivedItemSchemeItemsTree,
                                  childrenKey,
                                  node => node[childrenKey].find(child => child.id === item.id) !== undefined
                                ).length === 0
                              )
                            }
                            childrenKey={childrenKey}
                            rightActions={
                              <Button
                                onClick={() => {
                                  if (isItemsRemoveValid(derivedItemSchemeItems, selectedTargetItems)) {
                                    onItemsRemove(selectedTargetItems, childrenKey);
                                  } else {
                                    Modal.warning({
                                      title: t('reduxComponents.derivedItemsScheme.modals.warning.delete.title')
                                    })
                                  }
                                }}
                                icon="delete"
                                type="primary"
                                disabled={!selectedTargetItems || selectedTargetItems.length === 0}
                              >
                                {t('reduxComponents.derivedItemsScheme.buttons.removeItems.title')}
                              </Button>
                            }
                            itemTreeMaxDepth={derivedItemSchemeItemsTreeMaxDepth}
                            selectedItems={selectedTargetItems || []}
                            onSelectedItemsChange={selectedArr => onItemSelect(selectedArr, false)}
                          />
                        </Col>
                      </Row>
                    )
                    : (
                      <Row type="flex" gutter={GUTTER_MD}
                           style={{marginBottom: MARGIN_MD}}>
                        <Col span={SPAN_HALF}>
                          <InfiniteScrollTable
                            data={getNodes(itemTree, childrenKey, () => true)}
                            getRowKey={({id}) => id}
                            columns={columns}
                            multilangStrDataIndexes={["name"]}
                            height={TABLE_HEIGHT}
                            searchBarSpan={10}
                            rightActions={
                              <Button
                                onClick={() => onItemsAdd(itemTree, childrenKey)}
                                icon="double-right"
                                type="primary"
                                disabled={!selectedSourceItems || selectedSourceItems.length === 0}
                              >
                                {t('reduxComponents.derivedItemsScheme.buttons.addItems.title')}
                              </Button>
                            }
                            rowSelection={{
                              selectedRowKeys: (selectedSourceItems || []),
                              onChange: selectedArr => onItemSelect(selectedArr, true)
                            }}
                            getIsDisabledRow={({id}) => (derivedItemSchemeItems || []).map(({id}) => id).includes(id)}
                          />
                        </Col>
                        <Col span={SPAN_HALF}>
                          <InfiniteScrollTable
                            data={_.sortBy((derivedItemSchemeItems || []), [`order[${lang}]`])}
                            getRowKey={({id}) => id}
                            columns={columns}
                            multilangStrDataIndexes={["name"]}
                            height={TABLE_HEIGHT}
                            searchBarSpan={10}
                            rightActions={
                              <Button
                                onClick={() => {
                                  if (isItemsRemoveValid(derivedItemSchemeItems, selectedTargetItems)) {
                                    onItemsRemove(selectedTargetItems, childrenKey);
                                  } else {
                                    Modal.warning({
                                      title: t('reduxComponents.derivedItemsScheme.modals.warning.delete.title')
                                    })
                                  }
                                }}
                                icon="delete"
                                type="primary"
                                disabled={!selectedTargetItems || selectedTargetItems.length === 0}
                              >
                                {t('reduxComponents.derivedItemsScheme.buttons.removeItems.title')}
                              </Button>
                            }
                            rowSelection={{
                              selectedRowKeys: (selectedTargetItems || []),
                              onChange: selectedArr => onItemSelect(selectedArr, false)
                            }}
                          />
                        </Col>
                      </Row>
                    )}
                </div>
              </div>
              <Row type="flex" justify="end" gutter={GUTTER_SM}>
                <Col>
                  <Button
                    onClick={() => onItemsRemove(derivedItemSchemeItems.map(({id}) => id), childrenKey)}
                    disabled={derivedItemSchemeItems === null || derivedItemSchemeItems.length === 0}
                  >
                    {t('reduxComponents.derivedItemsScheme.buttons.emptyList.title')}
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    onClick={() => {
                      if (isItemsRemoveValid(derivedItemSchemeItems, [])) {
                        onCreateShow();
                      } else {
                        Modal.warning({
                          title: t('reduxComponents.derivedItemsScheme.modals.warning.parentMissing.title')
                        })
                      }
                    }}
                    disabled={derivedItemSchemeItems === null || derivedItemSchemeItems.length === 0}
                  >
                    {t('reduxComponents.derivedItemsScheme.buttons.create.title')}
                  </Button>
                </Col>
              </Row>
            </div>
            <EnhancedModal
              visible={isCreateVisible}
              title={t('reduxComponents.derivedItemsScheme.modals.derive.title')}
              width={MODAL_WIDTH_MD}
              withDataLanguageSelector
              onCancel={onCreateHide}
              footer={
                <div>
                  <Button onClick={onCreateHide}>{t('commons.buttons.close.title')}</Button>
                  <Button
                    type="primary"
                    onClick={() => {
                      if (isArtefactValid(derivedItemScheme)) {
                        onCreateSubmit(derivedItemScheme, derivedItemSchemeItems);
                        onStateReset();
                      } else {
                        Modal.warning({
                          title: t('commons.alerts.invalidForm.title'),
                          content: t('commons.alerts.invalidForm.content')
                        });
                      }
                    }}
                  >
                    {t('reduxComponents.derivedItemsScheme.buttons.deriveSubmit.title')}
                  </Button>
                </div>
              }
            >
              <ArtefactMinForm
                artefact={derivedItemScheme}
                agencies={agencies}
                onChange={onCreateChange}
                mode={ARTEFACT_MIN_FORM_MODE_CREATE}
              />
            </EnhancedModal>
          </Fragment>
        )
      }}
    </DataLanguageConsumer>
  );
}

export default compose(
  translate(),
  connect(mapStateToProps, mapDispatchToProps)
)(ReduxDerivedItemScheme);